<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { watch, ref, computed } from "vue";
import {
  AtomText,
  useToast,
  OrgRatingForm,
  type IOrgRatingFormEmits,
  SatElasticPanel,
  useDate,
} from "@chatfood/design-system";
import { ReviewPlatforms } from "../review-platforms";
import type { IReviewFormProps } from "./props";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";
import type { IDomainOrderReviewReason } from "~/domain/order-review-reason";
import { useDirectBackend } from "#imports";
import { useCustomerStore } from "~/stores/customer";

const css = bemBuilder("review-form");

const props = defineProps<IReviewFormProps>();

const { t } = useLocale();
const { route } = useNavigation();
const { toastUnexpectedError } = useToast();
const { orderingModeLabel } = useDomainLabel();
const { formatDatetime, getTimeFormat } = useDate();

const isLoading = ref(false);
const isReviewFormOn = ref(false);
const finishedReview = ref(false);

const isShareReviewOn = ref(false);

type Order = Awaited<
  ReturnType<ReturnType<typeof useDirectBackend>["orderRepo"]["pendingReview"]>
>;

const orderDetails = ref<Order>();

const reviewPlatforms = ref<{ type: string; url: string }[]>([]);
const comment = ref("");

const selectedRating = ref();

const ratingOptions = [
  t("components.review_form.rating.1"),
  t("components.review_form.rating.2"),
  t("components.review_form.rating.3"),
  t("components.review_form.rating.4"),
  t("components.review_form.rating.5"),
];

const selectOptions = computed<
  { id: IDomainOrderReviewReason; name: string }[]
>(() => {
  if (selectedRating.value > 2 || !selectedRating.value) return [];

  return [
    {
      id: "item-missing",
      name: t("components.review_form.reasons.item_missing"),
    },
    {
      id: "order-issue",
      name: t("components.review_form.reasons.order_issue"),
    },
    {
      id: "order-is-wrong",
      name: t("components.review_form.reasons.order_is_wrong"),
    },
    {
      id: "food-is-not-fresh",
      name: t("components.review_form.reasons.food_is_not_fresh"),
    },
    {
      id: "food-is-cold",
      name: t("components.review_form.reasons.food_is_cold"),
    },
    {
      id: "box-was-opened",
      name: t("components.review_form.reasons.box_was_opened"),
    },
    {
      id: "delivery-late",
      name: t("components.review_form.reasons.delivery_late"),
    },
    {
      id: "delivery-issue",
      name: t("components.review_form.reasons.delivery_issue"),
    },
    { id: "rude-staff", name: t("components.review_form.reasons.rude_staff") },
  ];
});

const { orderRepo } = useDirectBackend();
const customer = useCustomerStore();

const checkOrderPendingReview = async () => {
  try {
    const orderIdFromRoute = route.value?.query["review-order"] as string;

    const res = await orderRepo.pendingReview({
      businessId: props.businessId,
      orderId: orderIdFromRoute,
    });

    if (res) {
      orderDetails.value = res;
    }

    if (!orderDetails.value?.hasReviewPending && !orderDetails.value?.order) {
      return;
    }

    isReviewFormOn.value = true;
    reviewPlatforms.value = orderDetails.value.reviewPlatforms;
  } catch {
    // there is nothing to do
  }
};

const onSave = async (payload: IOrgRatingFormEmits["save"][0]) => {
  if (!orderDetails.value?.order) return;

  isLoading.value = true;

  comment.value = payload.comment;

  try {
    await orderRepo.addReview({
      orderId: orderDetails.value.order.orderId,
      rating: payload.rating,
      comment: payload.comment,
      ...(payload.rating < 3
        ? { reason: payload.option as IDomainOrderReviewReason }
        : {}),
    });

    isLoading.value = false;
    finishedReview.value = true;

    if (reviewPlatforms.value.length) {
      isShareReviewOn.value = true;
      return;
    }

    onClose();
  } catch {
    toastUnexpectedError();
  }
};

const onClose = () => {
  isReviewFormOn.value = false;
};

const skipReview = async () => {
  onClose();

  if (!finishedReview.value && orderDetails.value?.order) {
    await orderRepo.skipReview({ orderId: orderDetails.value.order.orderId });
  }
};

watch(
  () => customer.isLogged,
  (value) => {
    if (value) {
      checkOrderPendingReview();
    }
  },
  { immediate: true },
);

watch(
  () => isReviewFormOn.value,
  (val) => {
    if (val || isShareReviewOn.value) return;

    skipReview();
  },
);
</script>

<template>
  <SatElasticPanel
    v-model="isReviewFormOn"
    show-close
    padding="none"
    width-fit-content
  >
    <div :class="css()">
      <div v-if="!isShareReviewOn">
        <div
          v-if="orderDetails && orderDetails.order"
          :class="css('order-details')"
        >
          <AtomText tag="div" type="body-bold">
            {{
              t("components.review_form.order_uid_label", {
                orderUid: orderDetails.order.orderUid,
              })
            }}
          </AtomText>
          <AtomText tag="div" type="small" color="color-text-secondary">
            {{
              t("components.review_form.card_summary", {
                orderingMode: orderingModeLabel(
                  orderDetails.order.orderingMode,
                ),
                createdAt: formatDatetime(
                  String(orderDetails.order.createdAt),
                  `D MMM YYYY, ${getTimeFormat()}`,
                ),
              })
            }}
          </AtomText>
        </div>
        <OrgRatingForm
          :title="t('components.review_form.title')"
          :subheader="t('components.review_form.subheader')"
          :hint-message="t('components.review_form.hint_message')"
          :cta-label="t('components.review_form.cta_label')"
          :rating-messages="ratingOptions"
          :select-option-placeholder="
            t('components.review_form.select_option_placeholder')
          "
          :select-options="selectOptions"
          :is-loading="isLoading"
          @save="onSave"
          @selected-rating="(value) => (selectedRating = value)"
        />
      </div>
      <ReviewPlatforms
        v-else
        :platform-links="reviewPlatforms"
        :comment="comment"
        @close="onClose"
      />
    </div>
  </SatElasticPanel>
</template>

<style lang="scss" scoped>
.review-form {
  max-width: 100%;
  padding: var(--spacing-xl);
  margin: 0 auto;

  &__order-details {
    text-align: center;
    margin-bottom: var(--spacing-lg);
  }

  @media (width > $breakpoint-sm) {
    width: 500px;
  }
}
</style>
